import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RootState } from "../../store/indexRedux";
import {
  faChevronUp,
  faChevronDown,
  faArrowLeft,
  faLink,
  faClock,
  faCopy,
  faSpinner,
  faEnvelope,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchFluviusMandates,
  FluviusEan,
  createMandateLink,
  sendMandateLink,
  SendMandateLinkAllFailedError,
} from "../../api/mandates";
import { FluviusErrorState, mapFluviusError } from "../../utils/fluviusErrors";
import { logout } from "../utils/cognito";
import { MandateLinkRecipient, SendMandateLinkResponse } from "../../types/mandateTypes";
import InfoIcon from "../svg/InfoIcon";
import { ErrorToast } from "../ui/toastResponds/ErrorToast";
import { toastConfig } from "../config/toast.config";

interface SendResult {
  sent: number;
  failed: number;
  successNames: string[];
  failedEmails: string[];
}

// Sentinel the backend uses to mean "not one of this company's contacts, just a typed
// address". It carries no name, which is why it loses the dedupe tie-break below.
const MANUAL_CONTACT_ID = -1;

// The one dedupe key, shared verbatim with the backend guard: trim, then lowercase.
// Mail domains are case-insensitive and local parts effectively are too at every provider
// we send through, so `Ann@Acme.be ` and `ann@acme.be` are one person, one email.
const recipientKey = (email: string) => (email ?? "").trim().toLowerCase();

// A recipient plus where it came from, so a dropped duplicate can be reported on the exact
// input that produced it instead of vanishing.
type RecipientCandidate = MandateLinkRecipient & { manualIndex: number | null };

// Collapse a candidate list to ONE entry per unique address. Winner rule, a deterministic
// total order implemented identically on the backend:
//   1. a real contact (contactId !== MANUAL_CONTACT_ID) beats a manual entry;
//   2. otherwise the FIRST occurrence wins (stable).
// Rule 1 is load-bearing: the contact entry is the one carrying fullName/contactId, so
// keeping it is exactly what stops the confirmation degrading "Ann Smith" to a raw address
// when the admin both ticks Ann and types her address.
//
// Survivors come back in first-seen key order (the surviving *entry* may be a later index
// than the slot it occupies, when a contact displaces an earlier manual entry), alongside
// the candidate indices that lost, so the caller can surface them.
const dedupeRecipients = (
  candidates: RecipientCandidate[]
): { kept: RecipientCandidate[]; droppedIndices: number[] } => {
  const winnerIndexByKey = new Map<string, number>();
  const keyOrder: string[] = [];
  const droppedIndices: number[] = [];

  candidates.forEach((candidate, index) => {
    const key = recipientKey(candidate.email);
    const incumbentIndex = winnerIndexByKey.get(key);

    if (incumbentIndex === undefined) {
      winnerIndexByKey.set(key, index);
      keyOrder.push(key);
      return;
    }

    const incumbentIsManual = candidates[incumbentIndex].contactId === MANUAL_CONTACT_ID;
    const challengerIsContact = candidate.contactId !== MANUAL_CONTACT_ID;

    if (incumbentIsManual && challengerIsContact) {
      winnerIndexByKey.set(key, index);
      droppedIndices.push(incumbentIndex);
    } else {
      droppedIndices.push(index);
    }
  });

  return {
    kept: keyOrder.map((key) => candidates[winnerIndexByKey.get(key)!]),
    droppedIndices,
  };
};

// The two awaited network calls in handleSendLink, in order. Drives the progress
// checklist in SendingCard — each step maps to a real request, so nothing is faked.
type SendStage = "link" | "email";

// Shared by the success (200) and all-failed (500) paths, so both render from the same
// numbers. `results` is authoritative when present; the recipient-list fallback only
// applies when something actually went out, so a failed address is never shown as sent.
const toSendResult = (
  response: SendMandateLinkResponse,
  recipients: MandateLinkRecipient[]
): SendResult => {
  const nameByEmail = new Map(
    recipients.map((r) => [r.email.toLowerCase(), r.fullName.trim() || r.email])
  );
  const results = response.results ?? [];
  const failedEmails = results.filter((r) => !r.ok).map((r) => r.email);
  const successNames =
    results.length > 0
      ? results.filter((r) => r.ok).map((r) => nameByEmail.get(r.email.toLowerCase()) ?? r.email)
      : response.sent > 0
      ? recipients.map((r) => r.fullName.trim() || r.email)
      : [];

  return {
    sent: response.sent,
    failed: response.failed,
    successNames,
    failedEmails,
  };
};

type StepState = "pending" | "active" | "complete";

// Step row in the customer-switch StageRow idiom (CustomerSwitchModal.tsx:183-231 on the
// unmerged ft-customer-switch-admin): a dumb presenter taking an already-translated
// label plus a state, one icon branch per state, no logic of its own. Reimplemented
// inline rather than imported — StageRow is not exported and lives only on that branch,
// so importing it would couple this PR to another open PR for ~35 lines of markup and
// break the moment either branch moves.
//
// Added here, all of which StageRow lacks: dark-mode variants, sr-only state text (there
// the state is carried by icon shape and colour alone, i.e. invisible to a screen
// reader), motion-reduce on the spinner, and muted text that actually passes WCAG AA —
// StageRow's pending `text-gray-400` is 2.54:1 on white, and the version this replaces
// stacked a row-level `opacity-50` on `text-gray-500` for 1.97:1.
const SendStepRow: React.FC<{ label: string; state: StepState }> = ({ label, state }) => {
  const { t } = useTranslation("management");

  const stateLabel =
    state === "complete"
      ? t("sendStepStateComplete")
      : state === "active"
      ? t("sendStepStateActive")
      : t("sendStepStatePending");

  return (
    <div className="flex items-center gap-3 py-2">
      <span className="flex items-center justify-center w-6 shrink-0">
        {state === "complete" ? (
          <svg
            className="w-5 h-5 text-green-600 dark:text-green-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M5 13l4 4L19 7" />
          </svg>
        ) : state === "active" ? (
          <svg
            className="w-5 h-5 text-primary-600 dark:text-primary-300 animate-spin motion-reduce:animate-none"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <span
            className="inline-block w-4 h-4 rounded-full border-2 border-gray-500 dark:border-primary-400"
            aria-hidden="true"
          />
        )}
      </span>
      <span
        className={`text-sm font-medium ${
          state === "complete"
            ? "text-primary-600 dark:text-primary-300"
            : state === "active"
            ? "text-primary-700 dark:text-primary-200"
            : "text-gray-600 dark:text-gray-400"
        }`}
      >
        {label}
        <span className="sr-only">{` — ${stateLabel}`}</span>
      </span>
    </div>
  );
};

// In-flight state for the "ask another person" flow: the two awaited calls in
// handleSendLink rendered as two rows. `stage` is set from those awaits, so a row is
// never "active" unless that request is genuinely in flight — no fabricated progress.
//
// The step list is a polite live region so the pending -> active -> complete transitions
// get spoken, and it is labelled by the visible heading so it can be navigated to by
// name. It cannot nest inside SendResultCard's region: the two are mutually exclusive
// branches of the same ternary and are never mounted at the same time.
//
// Deliberately NO `aria-busy` anywhere above the live region. `aria-busy="true"` tells
// AT to defer announcing changes inside that subtree until it clears — and here it would
// never clear, the block unmounts — so an ancestor `aria-busy` can silently swallow the
// very step announcements this region exists to make. There is no element in this flow
// that is both honestly "busy" and not an ancestor of the region, so the attribute is
// omitted rather than placed somewhere decorative. Busy-ness is already conveyed
// accessibly by the spinner plus each row's sr-only "in progress" text.
const SendingCard: React.FC<{ stage: SendStage }> = ({ stage }) => {
  const { t } = useTranslation("management");
  const headingId = useId();

  return (
    <div className="py-6">
      <h4 id={headingId} className="text-sm font-semibold text-primary-600 dark:text-white mb-3">
        {t("sendingLink")}
      </h4>
      <div role="status" aria-live="polite" aria-labelledby={headingId}>
        <SendStepRow
          label={t("sendStepCreatingLink")}
          state={stage === "link" ? "active" : "complete"}
        />
        <SendStepRow
          label={t("sendStepSendingEmails")}
          state={stage === "email" ? "active" : "pending"}
        />
      </div>
    </div>
  );
};

// Outcome card, rendered in place of the recipient picker once the send has resolved.
// It covers both outcomes: at least one email out (check icon, recipient names) and
// none out (warning icon, no "sent to N" count), and lists every bounced address in the
// same amber box in both cases.
//
// The mount-flag `scale`/`translate`/`opacity` transitions below are what animate it in.
// The whole card is a single live region, so a screen reader is given the outcome and
// the failed addresses as one announcement — role="status"/"alert" imply
// aria-atomic="true", which is why the amber box carries no live region of its own
// (nesting one inside another causes double or dropped announcements).
const SendResultCard: React.FC<{ result: SendResult; onDone: () => void }> = ({ result, onDone }) => {
  const { t } = useTranslation("management");
  const [mounted, setMounted] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const allFailed = result.sent === 0;

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    // The Send button unmounts with the picker, which drops focus to <body>. Move it to
    // the outcome heading so keyboard and screen-reader users land on the result instead
    // of at the top of the document.
    headingRef.current?.focus();
    return () => cancelAnimationFrame(id);
  }, []);

  const namesLabel = result.successNames.join(", ");

  return (
    <div
      className="text-center py-4"
      role={allFailed ? "alert" : "status"}
      aria-live={allFailed ? "assertive" : "polite"}
    >
      <div
        className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-5 transition-all duration-500 ease-out motion-reduce:transition-none ${
          allFailed ? "bg-amber-100 dark:bg-amber-900/30" : "bg-green-100 dark:bg-green-900/30"
        } ${mounted ? "scale-100 opacity-100" : "scale-50 opacity-0"}`}
      >
        <svg
          className={`w-9 h-9 transition-all duration-700 delay-150 ease-out ${
            allFailed ? "text-amber-600 dark:text-amber-400" : "text-green-600 dark:text-green-400"
          } ${mounted ? "scale-100 opacity-100" : "scale-0 opacity-0"}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          {allFailed ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.4}
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
          )}
        </svg>
      </div>

      <h4
        ref={headingRef}
        tabIndex={-1}
        className={`text-lg font-semibold text-primary-600 dark:text-white mb-2 transition-all duration-500 delay-200 ease-out focus:outline-none motion-reduce:transition-none ${
          mounted ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
        }`}
      >
        {allFailed ? t("linkSentNoneTitle") : t("linkSentTitle")}
      </h4>

      {!allFailed && (
        <p
          className={`text-sm text-primary-500 dark:text-primary-300 mb-1 transition-all duration-500 delay-300 ease-out ${
            mounted ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
          }`}
        >
          {t("linkSentCount", { count: result.sent })}
        </p>
      )}

      {result.successNames.length > 0 && (
        <p
          className={`text-sm text-primary-500 dark:text-primary-300 mb-4 transition-all duration-500 delay-300 ease-out ${
            mounted ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
          }`}
        >
          {t("linkSentToRecipients", { names: namesLabel })}
        </p>
      )}

      {result.failedEmails.length > 0 && (
        <div className="mx-auto max-w-md mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-400">
          {t("linkSentPartialFailed", { emails: result.failedEmails.join(", ") })}
        </div>
      )}

      <button
        onClick={onDone}
        className="mt-2 px-6 py-2 bg-primary-600 text-white rounded-3xl text-sm font-semibold hover:bg-primary-700 transition-colors"
      >
        {t("sendDone")}
      </button>
    </div>
  );
};

// Inline error box shared by the three Data Manager calls. A session-expired error gets a
// log-in action: logout() clears the session cookies and redirects to the login app.
const FluviusErrorBox: React.FC<{ error: FluviusErrorState; className?: string }> = ({ error, className = "" }) => {
  const { t } = useTranslation("management");
  return (
    <div className={`${className} p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400`}>
      {error.message}
      {error.isSessionExpired && (
        <button
          type="button"
          onClick={() => {
            void logout();
          }}
          className="ml-2 font-semibold underline hover:no-underline"
        >
          {t("logInAgain")}
        </button>
      )}
    </div>
  );
};

const FluviusManager: React.FC = () => {
  const { t } = useTranslation("management");
  const navigate = useNavigate();

  const selectedCompanyId = useSelector(
    (state: RootState) => state.selectedCompany.selectedUserCompany?.company_id
  );
  const nonAdminContacts = useSelector((state: RootState) =>
    (state.selectedCompany.selectedUserCompany?.users ?? []).filter(
      (u) => u.role !== "admin" && !u.email?.toLowerCase().includes("@yuso")
    )
  );
  const selectedKbo = selectedCompanyId ? String(selectedCompanyId) : null;
  const [eans, setEans] = useState<FluviusEan[]>([]);
  const [loadingMandates, setLoadingMandates] = useState(false);
  const [mandatesError, setMandatesError] = useState<FluviusErrorState | null>(null);
  const [eanSearch, setEanSearch] = useState("");
  const [expanded, setExpanded] = useState<string | false>(false);

  const [generatedUrl, setGeneratedUrl] = useState<{ url: string; validTo: string } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<FluviusErrorState | null>(null);
  const [copied, setCopied] = useState(false);

  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<number>>(new Set());
  const [manualEmails, setManualEmails] = useState<string[]>([""]);
  const [sending, setSending] = useState(false);
  const [sendStage, setSendStage] = useState<SendStage>("link");
  const [sendError, setSendError] = useState<FluviusErrorState | null>(null);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const trimmedManualEmails = manualEmails.map((e) => e.trim());
  const invalidManualEmailIndices = trimmedManualEmails
    .map((e, i) => (e.length > 0 && !emailRegex.test(e) ? i : -1))
    .filter((i) => i !== -1);

  // Everything the picker would send, pre-dedupe, in the order the guard resolves it:
  // ticked contacts first, then the typed addresses in input order. Built once here and
  // reused by the payload, the duplicate warnings and the recipient count, so those three
  // can never disagree about who is being contacted.
  const recipientCandidates: RecipientCandidate[] = [
    ...nonAdminContacts
      .filter((c) => selectedContactIds.has(c.contact_id))
      .map((c) => ({
        contactId: c.contact_id,
        email: c.email,
        fullName: c.fullName,
        manualIndex: null,
      })),
    ...trimmedManualEmails.flatMap((email, index) =>
      email.length > 0 && emailRegex.test(email)
        ? [{ contactId: MANUAL_CONTACT_ID, email, fullName: "", manualIndex: index }]
        : []
    ),
  ];

  const { kept: dedupedCandidates, droppedIndices } = dedupeRecipients(recipientCandidates);

  // The payload. One entry per unique address — a known duplicate is never sent, so nobody
  // gets two emails and sales never gets two CCs for the same person.
  const recipients: MandateLinkRecipient[] = dedupedCandidates.map(
    ({ contactId, email, fullName }) => ({ contactId, email, fullName })
  );

  // Typed addresses that lost the dedupe (to a ticked contact, or to an earlier input).
  // Dropping input silently is its own confusion, so each one gets an inline warning.
  // A contact losing to another contact has no input to point at and no warning slot.
  const duplicateManualEmailIndices = droppedIndices
    .map((i) => recipientCandidates[i].manualIndex)
    .filter((i): i is number => i !== null);

  // People, not sends: drives both the send-button enablement and the "is anyone selected"
  // guard, and must therefore match the payload length exactly.
  const totalRecipientCount = recipients.length;

  const loadMandates = useCallback(async () => {
    if (!selectedCompanyId) return;
    setLoadingMandates(true);
    setMandatesError(null);
    try {
      const response = await fetchFluviusMandates(selectedCompanyId);
      setEans(response.eans);
    } catch (err) {
      console.error(err);
      setMandatesError(mapFluviusError(err, t, "errorLoadingMandates", { hasCompanyStatusMapping: true }));
    } finally {
      setLoadingMandates(false);
    }
  }, [t, selectedCompanyId]);

  useEffect(() => {
    loadMandates();
  }, [loadMandates, selectedCompanyId]);

  useEffect(() => {
    setGeneratedUrl(null);
    setGenerateError(null);
    setShowRecipientPicker(false);
    setSelectedContactIds(new Set());
    setManualEmails([""]);
    setSendError(null);
    setSendResult(null);
  }, [selectedCompanyId]);

  const filteredEans = eanSearch
    ? eans.filter((e) => e.ean.toLowerCase().includes(eanSearch.toLowerCase()))
    : eans;

  const canGenerate = !!selectedKbo;

  const handleGenerateConsentUrl = async () => {
    if (!selectedKbo) return;
    setShowRecipientPicker(false);
    setSelectedContactIds(new Set());
    setSendError(null);
    if (generatedUrl) return;
    setGenerating(true);
    setGenerateError(null);
    try {
      const result = await createMandateLink({
        referenceNumber: `API - ${selectedKbo} - ${Date.now()}`,
        flow: "B2B",
        dataServices: ["VH_kwartier_uur"],
        enterpriseNumber: selectedKbo,
        numberOfEans: 100,
      });
      setGeneratedUrl({
        url: result.approvalLink,
        validTo: result.validTo ? new Date(result.validTo).toLocaleDateString() : "N/A",
      });
    } catch (err) {
      console.error(err);
      setGenerateError(mapFluviusError(err, t, "errorGenerating"));
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCancelPicker = () => {
    setShowRecipientPicker(false);
    setSelectedContactIds(new Set());
    setManualEmails([""]);
    setSendError(null);
    setSendResult(null);
  };

  const toggleContactSelection = (contactId: number) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  const handleAskAnotherPerson = () => {
    setSelectedContactIds(new Set());
    setManualEmails([""]);
    setSendError(null);
    setSendResult(null);
    setShowRecipientPicker(true);
  };

  const updateManualEmail = (index: number, value: string) => {
    setManualEmails((prev) => prev.map((e, i) => (i === index ? value : e)));
  };

  const addManualEmail = () => {
    setManualEmails((prev) => [...prev, ""]);
  };

  const removeManualEmail = (index: number) => {
    setManualEmails((prev) => (prev.length === 1 ? [""] : prev.filter((_, i) => i !== index)));
  };

  const handleSendLink = async () => {
    if (!selectedKbo || !selectedCompanyId || totalRecipientCount === 0 || invalidManualEmailIndices.length > 0) return;
    // `recipients` is the deduped list built during render — a duplicate is a warning, not a
    // blocker, so the send proceeds with the address counted exactly once.

    setSending(true);
    setSendStage("link");
    setSendError(null);
    setSendResult(null);
    try {
      const generated = await createMandateLink({
        referenceNumber: `API - ${selectedKbo} - ${Date.now()}`,
        flow: "B2B",
        dataServices: ["VH_kwartier_uur"],
        enterpriseNumber: selectedKbo,
        numberOfEans: 100,
      });

      setSendStage("email");

      const response = await sendMandateLink({
        companyId: selectedCompanyId,
        url: generated.approvalLink,
        validTo: generated.validTo ?? null,
        recipients,
      });

      setSendResult(toSendResult(response, recipients));
    } catch (err) {
      console.error(err);
      // Every recipient bounced. The backend answers 500 but still reports which
      // addresses failed and why, so show that in the same card as a partial failure
      // rather than collapsing it into a generic "please try again".
      if (err instanceof SendMandateLinkAllFailedError) {
        setSendResult(toSendResult(err.response, recipients));
        return;
      }
      const mapped = mapFluviusError(err, t, "errorSendingLink");
      setSendError(mapped);
      toast(<ErrorToast title="Error" message={mapped.message} />, {
        ...toastConfig,
        autoClose: 5000,
        icon: false,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="my-20 md:mx-14 text-primary-600 dark:text-primary-300">
      <button
        onClick={() => navigate("/management")}
        className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-600 dark:text-primary-300 dark:hover:text-white mb-6 transition-colors"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        {t("backToManagement")}
      </button>
      <h1 className="text-3xl font-semibold dark:text-white mb-10">{t("fluviusManager")}</h1>

      <div className="mb-6 max-w-2xl">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            className="px-5 py-2 bg-primary-600 text-white rounded-3xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors"
            disabled={!canGenerate || generating || (!!generatedUrl && !showRecipientPicker)}
            onClick={handleGenerateConsentUrl}
          >
            {generating && <FontAwesomeIcon icon={faSpinner} spin />}
            {generating ? t("generatingRequest") : t("requestMandate")}
          </button>
          <button
            className="px-5 py-2 bg-primary-600 text-white rounded-3xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors"
            disabled={!canGenerate || sending || showRecipientPicker}
            onClick={handleAskAnotherPerson}
          >
            <FontAwesomeIcon icon={faEnvelope} />
            {t("askAnotherPerson")}
          </button>
        </div>

        {showRecipientPicker && (
          <div className="mt-6 p-5 rounded-lg border border-gray-200 dark:border-primary-800 bg-base-100 dark:bg-primary-950">
            {sendResult ? (
              <SendResultCard result={sendResult} onDone={handleCancelPicker} />
            ) : sending ? (
              <SendingCard stage={sendStage} />
            ) : (
              <>
            <h5 className="text-sm font-semibold text-primary-600 dark:text-white mb-3">
              {t("sendTo")}
            </h5>
            {nonAdminContacts.length === 0 ? (
              <p className="text-sm text-primary-400 dark:text-primary-500 mb-4">
                {t("noNonAdminContacts")}
              </p>
            ) : (
              <ul className="space-y-2 mb-4">
                {nonAdminContacts.map((c) => (
                  <li key={c.contact_id} className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id={`fluvius-recipient-${c.contact_id}`}
                      className="checkbox checkbox-sm"
                      checked={selectedContactIds.has(c.contact_id)}
                      onChange={() => toggleContactSelection(c.contact_id)}
                      disabled={sending}
                    />
                    <label
                      htmlFor={`fluvius-recipient-${c.contact_id}`}
                      className="flex-1 flex items-center justify-between gap-3 text-sm text-primary-600 dark:text-primary-300 cursor-pointer"
                    >
                      <span className="font-medium">{c.fullName.trim() || c.email}</span>
                      <span className="font-mono text-xs text-primary-400 dark:text-primary-500">
                        {c.email}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-5 pt-5 border-t border-gray-200 dark:border-primary-800">
              <h5 className="text-sm font-semibold text-primary-600 dark:text-white mb-3">
                {t("additionalEmails")}
              </h5>
              <div className="space-y-2 mb-3">
                {manualEmails.map((email, index) => {
                  const isInvalid = invalidManualEmailIndices.includes(index);
                  // A duplicate is a WARNING, not an error: the address IS still being
                  // contacted (via the ticked contact or the earlier input), it is just
                  // being sent once instead of twice. So it gets amber rather than red and
                  // it deliberately does NOT disable Send — blocking to force a manual
                  // deletion would be punitive. An invalid address can never reach the
                  // candidate list, so the two states are mutually exclusive.
                  const isDuplicate = !isInvalid && duplicateManualEmailIndices.includes(index);
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <div className="flex-1">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => updateManualEmail(index, e.target.value)}
                          disabled={sending}
                          placeholder="email@example.com"
                          className={`input input-sm input-bordered w-full dark:bg-primary-900 ${isInvalid ? "border-red-500 dark:border-red-500" : isDuplicate ? "border-amber-500 dark:border-amber-500" : ""}`}
                        />
                        {isInvalid && (
                          <span className="text-xs text-red-500 dark:text-red-400">{t("invalidEmail")}</span>
                        )}
                        {isDuplicate && (
                          <span className="text-xs text-amber-600 dark:text-amber-400">{t("duplicateEmail")}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeManualEmail(index)}
                        disabled={sending}
                        aria-label="Remove email"
                        className="shrink-0 text-gray-400 hover:text-red-500 transition-colors p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={addManualEmail}
                disabled={sending}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-700 dark:text-primary-300 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faPlus} />
                {t("addEmail")}
              </button>
            </div>

            {sendError && <FluviusErrorBox error={sendError} className="mb-3 mt-4" />}

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handleSendLink}
                disabled={sending || totalRecipientCount === 0 || invalidManualEmailIndices.length > 0}
                className="px-5 py-2 bg-primary-600 text-white rounded-3xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors"
              >
                {sending && <FontAwesomeIcon icon={faSpinner} spin />}
                {t("send")}
              </button>
              <button
                onClick={handleCancelPicker}
                disabled={sending}
                className="px-4 py-2 text-sm font-semibold text-primary-500 hover:text-primary-700 dark:text-primary-300 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("cancel")}
              </button>
            </div>
              </>
            )}
          </div>
        )}

        {!selectedKbo && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
            {t("errorCompanyMissingKbo")}
          </div>
        )}

        {generateError && <FluviusErrorBox error={generateError} className="mt-4" />}

        {generatedUrl && !showRecipientPicker && (
          <div className="mt-6 p-5 rounded-lg border border-gray-200 dark:border-primary-800 bg-base-100 dark:bg-primary-950">
            <h4 className="font-semibold text-primary-600 dark:text-white flex items-center gap-2 mb-3">
              <FontAwesomeIcon icon={faLink} />
              {t("consentUrlGenerated")}
            </h4>
            <div className="bg-gray-50 dark:bg-primary-900 rounded-lg p-4 mb-4 break-all font-mono text-sm text-primary-600 dark:text-primary-300">
              {generatedUrl.url}
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <button
                onClick={() => handleCopy(generatedUrl.url)}
                className="px-4 py-2 bg-gray-200 dark:bg-primary-800 text-primary-600 dark:text-primary-300 rounded-3xl text-sm font-semibold hover:bg-gray-300 dark:hover:bg-primary-700 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faCopy} />
                {copied ? t("copied") : t("copyUrl")}
              </button>
              <span className="text-sm text-primary-400 dark:text-primary-500 flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} />
                {t("validUntil")} {generatedUrl.validTo}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Mandates List */}
      <div className="p-6 rounded-lg border-[1px] border-base-200 dark:border-primary-800 bg-base-100 dark:bg-primary-900">
        <h3 className="font-semibold text-primary-600 dark:text-white text-lg mb-2">
          {t("activeMandates")}
        </h3>
        <p className="text-sm text-primary-400 dark:text-primary-300 mb-4">
          {t("activeMandatesDescription")}
        </p>

        <input
          type="text"
          className="input input-bordered w-full max-w-xs dark:bg-primary-900 mb-4"
          value={eanSearch}
          onChange={(e) => setEanSearch(e.target.value)}
          placeholder={t("searchByEan")}
        />

        {loadingMandates ? (
          <div className="flex items-center gap-2 text-primary-400 py-4">
            <FontAwesomeIcon icon={faSpinner} spin />
            {t("loading")}
          </div>
        ) : mandatesError ? (
          <FluviusErrorBox error={mandatesError} />
        ) : filteredEans.length === 0 ? (
          <p className="text-sm text-primary-400 dark:text-primary-500 py-4">
            {eanSearch ? t("noMandatesMatchSearch") : t("noEansFound")}
          </p>
        ) : (
          <div>
            <table className="table table-sm w-full">
              <thead>
                <tr className="text-primary-500 dark:text-primary-300 border-b border-gray-200 dark:border-primary-800">
                  <th className="font-semibold">{t("ean")}</th>
                  <th className="font-semibold">{t("address")}</th>
                  <th className="font-semibold text-center">{t("dgo")}</th>
                  <th className="font-semibold text-center">
                    <span className="inline-flex items-center gap-1">
                      {t("status")}
                      <span
                        className="tooltip before:whitespace-normal before:w-52 before:text-[11px] before:leading-snug before:font-semibold before:bg-base-100 dark:before:bg-primary-900 before:text-primary-600 dark:before:text-primary-200 before:border before:border-base-200 dark:before:border-primary-800 before:shadow-sm after:!text-base-100 dark:after:!text-primary-900"
                        data-tip={t("statusTooltip")}
                      >
                        <InfoIcon />
                      </span>
                    </span>
                  </th>
                  <th className="font-semibold text-center">{t("validFrom")}</th>
                  <th className="font-semibold text-center">{t("validTo")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredEans.map((e) => {
                  const statusClass =
                    e.status === "Active"
                      ? "text-green-600"
                      : e.status === "NotAvailable"
                      ? "text-amber-500 dark:text-amber-400"
                      : "text-red-400";
                  const statusLabel =
                    e.status === "Active"
                      ? t("active")
                      : e.status === "NotAvailable"
                      ? t("unavailable")
                      : t("inactive");
                  return (
                    <React.Fragment key={e.ean}>
                      <tr className="border-b border-gray-100 dark:border-primary-800 text-primary-600 dark:text-primary-300">
                        <td className="font-mono text-sm">{e.ean.replace(/^EAN/i, "")}</td>
                        <td className="text-sm">{e.address}</td>
                        <td className="text-center text-sm">{e.dgo}</td>
                        <td className="text-center">
                          <span className={`inline-flex items-center gap-1 text-sm font-semibold ${statusClass}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td className="text-center text-sm">{e.validFrom ? new Date(e.validFrom).toLocaleDateString() : t("notAvailable")}</td>
                        <td className="text-center text-sm">{!e.validFrom ? t("notAvailable") : e.validTo ? new Date(e.validTo).toLocaleDateString() : "∞"}</td>
                        <td>
                          <button
                            className="cursor-pointer ml-2"
                            onClick={() => setExpanded(expanded === e.ean ? false : e.ean)}
                          >
                            <FontAwesomeIcon icon={expanded === e.ean ? faChevronUp : faChevronDown} />
                          </button>
                        </td>
                      </tr>

                      {expanded === e.ean && (
                        <tr>
                          <td colSpan={7} className="p-0">
                            <div className="bg-base-100 dark:bg-primary-950 rounded-lg border border-gray-100 dark:border-primary-800 m-2 p-4">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-primary-400 dark:text-primary-500">{t("referenceNumber")}:</span>
                                  <span className="ml-2 font-mono text-primary-600 dark:text-primary-300">{e.referenceNumber ?? t("notAvailable")}</span>
                                </div>
                                <div>
                                  <span className="text-primary-400 dark:text-primary-500">{t("dataService")}:</span>
                                  <span className="ml-2 text-primary-600 dark:text-primary-300">{e.dataServiceType ?? t("notAvailable")}</span>
                                </div>
                                <div>
                                  <span className="text-primary-400 dark:text-primary-500">{t("status")}:</span>
                                  <span className="ml-2 text-primary-600 dark:text-primary-300">{statusLabel}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FluviusManager;
